import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  makeStateKey,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  TransferState
} from '@angular/core';
import { ActivatedRoute, NavigationError, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { filter, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  Asset,
  GetActiveOrderQuery,
  GetStoreRevisionInfoQuery,
  GetStoreRevisionInfoQueryVariables,
  ValidatePreviewAccessQuery,
  ValidatePreviewAccessQueryVariables,
  Channel,
  ExternalServicesSettings,
  ReturnPolicy,
  OrderAddress,
} from './common/generated-types';
import { GET_STORE_REVISION_INFO, VALIDATE_PREVIEW_ACCESS } from './common/graphql/documents.graphql';
import { DataService } from './core/providers/data/data.service';
import { StateService } from './core/providers/state/state.service';
import { register } from 'swiper/element/bundle';
import { GET_ACTIVE_ORDER } from './core/providers/active/active.service.graphql';
import { NGXLogger } from 'ngx-logger';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { environment } from '../environments/environment';
import { parseMaybeJsonList, parseMaybeJsonValue } from './common/utils/safe-json-parser';
import {
  ColorConfig,
  HeaderMessageConfig,
  LogoSectionData,
  NavMenuOption,
  NavigationItem,
  SiteStyleData,
  SnsInfo } from './common/interfaces';
import { Title } from '@angular/platform-browser';
import { DEFAULT_HIDE_ACCOUNT_VALUE, FALLBACK_APPLE_TOUCH_ICON_URL, FALLBACK_ASSET_URL, FALLBACK_ICON_URL } from './common/constants';
import { colord } from 'colord';
import { SiteStyleType } from './common/enums';
import { SiteRoute } from './common/generated-types';
import { CombinedAnalyticsService } from './core/providers/analytics/combined-analytics.service';
import { StructuredDataService } from './core/providers/structured-data/structured-data.service';
import { AssetPreviewPipe } from './shared/pipes/asset-preview.pipe';
import { QueryParamService } from './core/providers/query-param/query-param.service';
import { ChannelTokenService } from './core/providers/channel-token/channel-token.service';
import { getContactUsMailtoHref, navigateByNavigationItem } from './common/utils/navigation-helpers';

register();
declare global {
  interface Window {
    displayPreferenceModal: () => void;
    Tawk_API?: Record<string, unknown>;
    Tawk_LoadStart?: Date;
  }
}


const IS_MOBILE_KEY = makeStateKey<boolean>('IS_MOBILE');
const STORE_IDENTIFIER_KEY = makeStateKey<string>('STORE_IDENTIFIER');
type StoreInfoResult = NonNullable<GetStoreRevisionInfoQuery['storeInfo']>;
type StoreInfoState = GetStoreRevisionInfoQuery['storeInfo'] | null;
const STORE_INFO_KEY = makeStateKey<StoreInfoState>('STORE_INFO');
const SITE_ROUTES_KEY = makeStateKey<SiteRoute[]>('SITE_ROUTES');
const SHOP_HOST_KEY = makeStateKey<string>('SHOP_HOST');
const EXTERNAL_SERVICES_SETTINGS_KEY = makeStateKey<ExternalServicesSettings>('EXTERNAL_SERVICES_SETTINGS');
const GTM_MODE_IS_SERVER_SIDE_KEY = makeStateKey<boolean>('GTM_MODE_IS_SERVER_SIDE');
const SHOW_PREVIEW_BANNER_KEY = makeStateKey<boolean>('SHOW_PREVIEW_BANNER');
const SHOW_REVIEW_BANNER_KEY = makeStateKey<boolean>('SHOW_REVIEW_BANNER');
const PREVIEW_ACCESS_GRANTED_KEY = makeStateKey<boolean>('PREVIEW_ACCESS_GRANTED');

interface StoreInfo {
  name: string;
  description?: string;
  storeUrl?: string;
  identifier: string;
  sellerChannelId: string;
  email: string;
  supportEmail?: string;
  mailAddressTo?: OrderAddress | null;
  snsInfo: SnsInfo | null;
  sellerChannel: Channel | null;
  returnPolicy?: ReturnPolicy;
  externalServicesSettings?: ExternalServicesSettings;
}

interface PageMessages {
  headerMessageConfig: HeaderMessageConfig;
  disclaimer: string;
  copyrightNotice: string;
}

interface UIState {
  isForStore: boolean;
  isSearchBarExpanded: boolean;
  isNavExpanded: boolean;
  isMobile: boolean | null;
  displayHeaderMenu: boolean;
  displayFooterMenu: boolean;
  displayHeaderMessage: boolean;
}

interface FooterNavigationColumn {
  name: string;
  items: NavigationItem[];
}

type ChannelTokenStatus = 'ok' | 'missing' | 'unsupported';

@Component({
  selector: 'vsf-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  // Observables
  cartDrawerVisible$: Observable<boolean>;
  mobileNavVisible$: Observable<boolean>;
  slugOrIdentifier$: Observable<{ type: string; slug: string }>;
  returnRequestForm$: Observable<boolean>;
  showReturnForm: boolean | undefined = false;
  isPublished = true;
  storeStatus: string | null = null;
  previewToken: string | null = null;
  showPreviewBanner = false;
  showReviewBanner = false;
  previewAccessGranted = false;
  private currentQueryParams: Record<string, string> | null = null;
  channelInfo$: any;
  readyToDisplay$ = new BehaviorSubject<boolean>(false);
  // Direct properties
  storeInfo: StoreInfo = {
    name: '',
    identifier: '',
    sellerChannelId: '',
    sellerChannel: null,
    email: '',
    supportEmail: '',
    snsInfo: null,
    returnPolicy: undefined,
    externalServicesSettings: undefined,
  };
  pageMessages: PageMessages = {
    headerMessageConfig: {
      displayOrder: 'sequence',
      switchType: 'manual',
      autoSwitchInterval: 3,
      messageList: [],
    },
    disclaimer: '',
    copyrightNotice: '',
  };
  storeColor: ColorConfig | null = null;
  storeLogoConfig: LogoSectionData = {
    pageTitle: '',
    displayHeaderMenu: true,
    displayFooterMenu: true,
    normalLogo: null,
    footerLogo: null,
    smallLogo: null,
    nameLogo: null,
    displayName: false,
    browserIcon: null,
    webclipIcon: null,
  };
  storeNavMenu: NavMenuOption[] = [];
  mobileSecondaryNavMenu: NavMenuOption[] = [];
  uiState: UIState = {
    isForStore: false,
    displayHeaderMessage: true,
    displayHeaderMenu: true,
    displayFooterMenu: true,
    isSearchBarExpanded: false,
    isNavExpanded: false,
    isMobile: null,
  };
  navigation: any;
  baseUrl:string|null = null;
  hideAccount = DEFAULT_HIDE_ACCOUNT_VALUE;
  navigationColumns: FooterNavigationColumn[] = [];
  orderStatusHref = '';
  currentPath = '';
  copyrightNotice = '';

  // vimeo fullscreen preview
  fullscreenPreviewOpen$: Observable<number | null>;
  vimeoVideoId: number | null = null;
  currentStoreIdentifier = '';
  storeIdentifier$: Observable<string | null>;
  hydratedInBrowser = false;
  readonly isBrowser: boolean;

  warning$!: Observable<{ title: string; body: string; showRefresh: boolean } | null>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private stateService: StateService,
    private dataService: DataService,
    private structuredDataService: StructuredDataService,
    private changeDetector: ChangeDetectorRef,
    private combinedAnalyticsService: CombinedAnalyticsService,
    private channelTokenService: ChannelTokenService,
    private titleService: Title,
    private logger: NGXLogger,
    private transferState: TransferState,
    private queryParamService: QueryParamService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject('IS_MOBILE') private isMobileDeviceFromSSR: boolean | null,
    @Optional() @Inject('STORE_IDENTIFIER') public storeIdentifierFromSSR: string | null,
    @Optional() @Inject('SHOP_HOST') public shopHost: string | null,
    @Optional() @Inject('GTM_MODE_IS_SERVER_SIDE') private gtmModeIsServerSideFromSSR: boolean | null,
    @Optional() @Inject('PREVIEW_QUERY_TOKEN') private previewQueryTokenFromSSR: string | null
  ) {
    this.warning$ = this.channelTokenService.status$.pipe(
      map((s: ChannelTokenStatus) => {
        if (s === 'unsupported') {
          return {
            title: 'This browser cannot complete checkout',
            body: 'Your browser blocks required storage. Please try a different browser or disable strict privacy mode.',
            showRefresh: false,
          };
        }
        if (s === 'missing') {
          return {
            title: 'Store information is missing',
            body: 'Please refresh the page, then continue checkout.',
            showRefresh: true,
          };
        }
        return null;
      }),
    );
    this.baseUrl = '';
    this.isBrowser = isPlatformBrowser(platformId);
    this.hideAccount = typeof environment.hideAccount === 'boolean' ? environment.hideAccount : DEFAULT_HIDE_ACCOUNT_VALUE;
    if (isPlatformBrowser(platformId)) {
      this.router.events.pipe(filter(e => e instanceof NavigationError)).subscribe(e => {
        const err = (e as NavigationError).error;
        if (err && err.name === 'ChunkLoadError') {
          window.location.assign((e as NavigationError).url);
        }
      });
    }
  }

  async ngOnInit(): Promise<void> {
    this.logger.debug('AppComponent - ngOnInit', this.storeIdentifierFromSSR, this.isMobileDeviceFromSSR, this.shopHost);
    const currentUrl = isPlatformBrowser(this.platformId)
      ? `${window.location.pathname}${window.location.search}`
      : this.router.url;
    this.currentPath = currentUrl.split('?')[0];
    this.logger.debug(`AppComponent - currentPath: ${this.currentPath} url: ${currentUrl}`);
    this.queryParamService.captureFromUrl(currentUrl);
    const urlParts = currentUrl.split('?');
    if (urlParts.length > 1) {
      const params = new URLSearchParams(urlParts[1]);
      const queryParams: Record<string, string> = {};
      params.forEach((value, key) => {
        queryParams[key] = value;
      });
      this.currentQueryParams = queryParams;
    }
    if (this.currentPath.includes('return-form')) {
      this.showReturnForm = true;
    } else {
      this.showReturnForm = false;
    }
    if (this.showReturnForm) {
      return;
    }
    this.stateService.setState('storePath', this.baseUrl || '');
    this.initializePlatformState();
    if (this.currentStoreIdentifier !== '') {
      this.uiState.isForStore = true;
      await this.initializeStoreInfo();
    }
    this.cartDrawerVisible$ = this.stateService.select(state => state.cartDrawerOpen).pipe(
      startWith(false)
    );
    const status = this.storeStatus;
    const previewParamToken = isPlatformServer(this.platformId)
      ? this.previewQueryTokenFromSSR ?? undefined
      : this.currentQueryParams?.['preview'];

    if (status === 'closed') {
      if (isPlatformBrowser(this.platformId)) {
        this.router.navigate([this.baseUrl, 'not-found']);
      }
      this.changeDetector.detectChanges();
      return;
    } else if (status === 'published') {
      await this.combinedAnalyticsService.initialize();
      this.showPreviewBanner = false;
      this.showReviewBanner = false;
    } else if (status === 'preview') {
      const hasAccess = isPlatformServer(this.platformId)
        ? await this.enablePreviewModeForSsr(previewParamToken, 'preview')
        : previewParamToken
          ? await this.applyPreviewAccessToken(previewParamToken, 'preview')
          : await this.restorePreviewAccessToken('preview');

      if (!hasAccess && isPlatformBrowser(this.platformId)) {
        this.router.navigate([this.baseUrl, previewParamToken ? 'not-found' : 'under-construction']);
        this.changeDetector.detectChanges();
        return;
      }
    } else if (status === 'review') {
      const hasAccess = isPlatformServer(this.platformId)
        ? await this.enablePreviewModeForSsr(previewParamToken, 'review')
        : previewParamToken
          ? await this.applyPreviewAccessToken(previewParamToken, 'review')
          : await this.restorePreviewAccessToken('review');

      if (!hasAccess && isPlatformBrowser(this.platformId)) {
        this.router.navigate([this.baseUrl, 'under-construction']);
        this.changeDetector.detectChanges();
        return;
      }
    } else {
      // 'new' or unknown
      if (isPlatformBrowser(this.platformId)) {
        this.router.navigate([this.baseUrl, 'under-construction']);
      }
      this.changeDetector.detectChanges();
      return;
    }
    // get the vimeo video id from the state
    this.fullscreenPreviewOpen$ = this.stateService.select(state => state.fullscreenPreviewOpen)
      .pipe(switchMap(id => {
        this.vimeoVideoId = id;
        return of(id);
    }));
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      queueMicrotask(() => {
        this.hydratedInBrowser = true;
        this.changeDetector.detectChanges();
      });
    }
  }

  private initializePlatformState(): void {
    if (isPlatformServer(this.platformId)) {
      this.transferState.set(STORE_IDENTIFIER_KEY, this.storeIdentifierFromSSR || '');
      this.transferState.set(IS_MOBILE_KEY, this.isMobileDeviceFromSSR ?? true);
      this.transferState.set(SHOP_HOST_KEY, this.shopHost);
      this.transferState.set(GTM_MODE_IS_SERVER_SIDE_KEY, !!this.gtmModeIsServerSideFromSSR);

      this.currentStoreIdentifier = this.storeIdentifierFromSSR || '';
      this.uiState.isMobile = this.isMobileDeviceFromSSR ?? true;
      this.stateService.setState('gtmModeIsServerSide', !!this.gtmModeIsServerSideFromSSR);
    } else {
      this.uiState.isMobile = this.transferState.get(IS_MOBILE_KEY, true);
      this.currentStoreIdentifier = this.transferState.get(STORE_IDENTIFIER_KEY, '');
      this.shopHost = this.transferState.get(SHOP_HOST_KEY, `https://${this.currentStoreIdentifier}.${environment.shopDomain}`);
      const gtmMode = this.transferState.get(GTM_MODE_IS_SERVER_SIDE_KEY, false);
      this.stateService.setState('gtmModeIsServerSide', gtmMode);

      this.showPreviewBanner = this.transferState.get(SHOW_PREVIEW_BANNER_KEY, false);
      this.showReviewBanner = this.transferState.get(SHOW_REVIEW_BANNER_KEY, false);
      this.previewAccessGranted = this.transferState.get(PREVIEW_ACCESS_GRANTED_KEY, false);

      this.transferState.remove(STORE_IDENTIFIER_KEY);
      this.transferState.remove(IS_MOBILE_KEY);
      this.transferState.remove(SHOP_HOST_KEY);
      this.transferState.remove(GTM_MODE_IS_SERVER_SIDE_KEY);
      this.transferState.remove(SHOW_PREVIEW_BANNER_KEY);
      this.transferState.remove(SHOW_REVIEW_BANNER_KEY);
      this.transferState.remove(PREVIEW_ACCESS_GRANTED_KEY);
    }

    this.stateService.setState('storeIdentifier', this.currentStoreIdentifier);
    this.stateService.setState('isMobile', this.uiState.isMobile);
    this.stateService.setState('shopHost', this.shopHost || `https://${this.currentStoreIdentifier}.${environment.shopDomain}`);
  }

  private async initializeStoreInfo(): Promise<void> {
    if (isPlatformServer(this.platformId)) {
      await this.initializeStoreInfoFromServer();
    } else {
      this.initializeStoreInfoFromTransferState();
    }
  }

  private initializeStoreInfoFromServer(): Promise<void> {
    return new Promise((resolve) => {
      this.dataService.query<GetStoreRevisionInfoQuery, GetStoreRevisionInfoQueryVariables>(
        GET_STORE_REVISION_INFO, { identifier: this.currentStoreIdentifier }
      ).pipe(take(1)).subscribe(({ storeInfo }) => {
        this.transferState.set(STORE_INFO_KEY, storeInfo);
        this.transferState.set(SITE_ROUTES_KEY, storeInfo?.siteRoutes);

        const externalServicesSettings = storeInfo?.externalServicesSettings;
        if (externalServicesSettings) {
          this.transferState.set(EXTERNAL_SERVICES_SETTINGS_KEY, externalServicesSettings);
          this.stateService.setState('externalServicesSettings', externalServicesSettings);
        }

        this.processStoreInfo(storeInfo);
        resolve();
      });
    });
  }

  private initializeStoreInfoFromTransferState(): void {
    const externalServicesSettings = this.transferState.get(EXTERNAL_SERVICES_SETTINGS_KEY, null);
    if (externalServicesSettings) {
      this.stateService.setState('externalServicesSettings', externalServicesSettings);
      this.loadClarityScript(externalServicesSettings);
      this.loadTawkioScript(externalServicesSettings);
    }

    const storeInfo = this.transferState.get(STORE_INFO_KEY, null);
    const siteRoutes = this.transferState.get(SITE_ROUTES_KEY, []);

    this.stateService.setState('siteRoutes', siteRoutes);
    this.processStoreInfo(storeInfo);
  }

  private processStoreInfo(storeInfo: StoreInfoState): void {
    const status = storeInfo?.status || (storeInfo?.isPublished ? 'published' : 'new');
    this.storeStatus = status;
    this.stateService.setState('storeStatus', status);
    this.isPublished = status === 'published';
    this.stateService.setState('isPublished', this.isPublished);
    this.stateService.setState('storeInfo', storeInfo);

    const visibleStatuses = ['published', 'preview', 'review'];
    if (storeInfo && visibleStatuses.includes(status)) {
      this.updateHeaderInfo('store', storeInfo);
    }
    if (isPlatformServer(this.platformId)) {
      this.stateService.setState('siteRoutes', storeInfo?.siteRoutes);
    }
  }

  private updateHeaderInfo(type: string, site: Partial<StoreInfoResult>): void {
    this.logger.debug(`${type.charAt(0).toUpperCase() + type.slice(1)} Header Info:${JSON.stringify(site)}`);
    this.setStoreData(site);
    this.uiState.isForStore = !!this.storeInfo.identifier;
    this.storeLogoConfig?.browserIcon && this.setFavicon(this.storeLogoConfig.browserIcon);
    this.storeLogoConfig?.webclipIcon && this.setWebClipIcon(this.storeLogoConfig.webclipIcon);
    this.setupOrderStatusHref();
    this.structuredDataService.replaceOne('organization', this.buildStoreJsonLd());
    this.structuredDataService.replaceOne('website', this.buildWebsiteJsonLd());
    this.readyToDisplay$.next(true);
  }

  private setStoreData(site: Partial<StoreInfoResult>): void {
    this.storeInfo.name = site.name || '';
    this.storeInfo.mailAddressTo = site.contactUsAddress;
    this.storeInfo.returnPolicy = site.returnPolicy;
    this.storeInfo.externalServicesSettings = site.externalServicesSettings;
    this.storeInfo.description = site.description || '';
    this.storeInfo.storeUrl = site?.storeUrl || '';
    this.storeInfo.identifier = site?.identifier || '';
    this.storeInfo.email = site?.sellerChannel?.customFields?.notificationEmail || '';
    this.storeInfo.supportEmail = site?.supportEmail || site?.sellerChannel?.customFields?.notificationEmail || '';
    this.storeInfo.sellerChannelId = site?.sellerChannel?.id || '';
    const headerMessageData = parseMaybeJsonValue<HeaderMessageConfig>(site?.headerMessageConfig, this.logger);
    if(headerMessageData && headerMessageData?.messageList?.length > 0) {
      this.pageMessages.headerMessageConfig = headerMessageData;
    }
    const siteStyles = parseMaybeJsonValue<SiteStyleData>(site.siteStyles, this.logger);
    this.pageMessages.copyrightNotice = siteStyles?.copyrightNotice || '';
    this.pageMessages.disclaimer = siteStyles?.disclaimer || '';
    this.uiState.displayHeaderMessage = siteStyles?.displayHeaderMessage ?? true;
    this.uiState.displayHeaderMenu = (siteStyles?.displayHeaderMenu === false) ? false : true;
    this.uiState.displayFooterMenu = (siteStyles?.displayFooterMenu === false) ? false : true;
    if (siteStyles?.siteTitle) {
      this.titleService.setTitle(siteStyles?.siteTitle);
    }
    this.storeLogoConfig.pageTitle = siteStyles?.siteTitle || '';
    this.storeLogoConfig.displayHeaderMenu = siteStyles?.displayHeaderMenu ?? true;
    this.storeLogoConfig.displayFooterMenu = siteStyles?.displayFooterMenu ?? true;
    this.storeLogoConfig.displayName = siteStyles?.displayStoreName ?? false;
    const hasTopMessage = !!(headerMessageData?.messageList?.length && this.uiState.displayHeaderMessage);
    this.stateService.setState('hasTopMessage', hasTopMessage);
    this.storeColor = parseMaybeJsonValue<ColorConfig>(site?.colorConfig, this.logger) || null;
    if(this.storeColor) {
      this.applyThemeColors(this.storeColor);
    }
    this.storeInfo.snsInfo = parseMaybeJsonValue<SnsInfo>(site?.snsInfoSection, this.logger) || null;
    this.storeNavMenu = parseMaybeJsonList<NavMenuOption>(site?.navigationMenu, this.logger);
    this.mobileSecondaryNavMenu = parseMaybeJsonList<NavMenuOption>(site?.mobileSecondaryNavigationMenu, this.logger);
    const logoSection = site?.logoSectionData;
    if (logoSection) {
      this.storeLogoConfig.normalLogo = logoSection.normalLogo as Asset;
      this.storeLogoConfig.footerLogo = logoSection.footerLogo as Asset;
      this.storeLogoConfig.smallLogo = logoSection.normalLogo as Asset;
      this.storeLogoConfig.nameLogo = logoSection.nameLogo as Asset;
      this.storeLogoConfig.browserIcon = logoSection.browserIcon as Asset;
      this.storeLogoConfig.webclipIcon = logoSection.webclipIcon as Asset;
      this.logger.debug(
        `storeLogo: ${this.storeLogoConfig.normalLogo}, storeNameLogo: ${this.storeLogoConfig.nameLogo}`
      );
    }
    if(site.footerNavigationColumns && site.footerNavigationColumns.length > 0) {
      this.navigationColumns = parseMaybeJsonList<FooterNavigationColumn>(site.footerNavigationColumns, this.logger);
    }
  }

  private setupOrderStatusHref(): void {
    this.stateService.select(state => state.signedIn).pipe(
      takeUntil(this.destroy$),
      map(isSignedIn => isSignedIn ? `${this.baseUrl}/account/orders` : `${this.baseUrl}/orderstatus`)
    ).subscribe(href => {
      this.orderStatusHref = href;
      this.changeDetector.detectChanges();
    });
  }

  private buildStoreJsonLd(): object {
    const mailtoAddress = this.storeInfo.mailAddressTo;
    const iconPreviewPipe = new AssetPreviewPipe();
    const data = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "url": `${this.storeInfo.storeUrl}`,
      "logo": `${this.storeInfo.storeUrl}${iconPreviewPipe.transform(this.storeLogoConfig.normalLogo as Asset, 'small', 'jpeg')}`,
      "name": this.storeInfo.name,
      "description": this.storeInfo.description || '',
      "email": this.storeInfo.email,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": mailtoAddress?.streetLine1,
        "addressLocality": mailtoAddress?.city,
        "addressCountry": mailtoAddress?.countryCode,
        "addressRegion": mailtoAddress?.province,
        "postalCode": mailtoAddress?.postalCode,
      },
    };
    return data;
  }

  private buildWebsiteJsonLd(): object {
    const data = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "url": `${this.storeInfo.storeUrl}`,
      "name": this.storeInfo.name,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${this.storeInfo.storeUrl}/search?search={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    };
    return data;
  }

  private async validatePreviewToken(token: string): Promise<boolean> {
    try {
      const { validatePreviewAccess } = (await this.dataService
        .query<ValidatePreviewAccessQuery, ValidatePreviewAccessQueryVariables>(
          VALIDATE_PREVIEW_ACCESS,
          { identifier: this.currentStoreIdentifier, token },
          'network-only'
        ).pipe(take(1)).toPromise())!;
      if (validatePreviewAccess && isPlatformBrowser(this.platformId)) {
        sessionStorage.setItem('previewToken', token);
      }
      return validatePreviewAccess;
    } catch {
      return false;
    }
  }


  private async enablePreviewModeForSsr(token: string | undefined, mode: 'preview' | 'review'): Promise<boolean> {
    if (!token) {
      return false;
    }

    const valid = await this.validatePreviewToken(token);
    if (!valid) {
      // Clear sensitive store data from transfer state to prevent info leakage to the browser.
      this.transferState.remove(STORE_INFO_KEY);
      this.transferState.remove(SITE_ROUTES_KEY);
      this.transferState.remove(EXTERNAL_SERVICES_SETTINGS_KEY);
      this.previewAccessGranted = false;
      this.previewToken = null;
      this.stateService.setState('previewToken', null);
      return false;
    }

    this.previewToken = token;
    this.stateService.setState('previewToken', token);
    this.setPreviewMode(mode);
    this.previewAccessGranted = true;
    // Transfer flags so the browser's initial hydration paint matches SSR HTML.
    this.transferState.set(SHOW_PREVIEW_BANNER_KEY, this.showPreviewBanner);
    this.transferState.set(SHOW_REVIEW_BANNER_KEY, this.showReviewBanner);
    this.transferState.set(PREVIEW_ACCESS_GRANTED_KEY, true);
    return true;
  }

  private setPreviewMode(mode: 'preview' | 'review'): void {
    this.showPreviewBanner = mode === 'preview';
    this.showReviewBanner = mode === 'review';
  }

  private async applyPreviewAccessToken(token: string, mode: 'preview' | 'review'): Promise<boolean> {
    const valid = await this.validatePreviewToken(token);
    if (!valid) {
      this.showPreviewBanner = false;
      this.showReviewBanner = false;
      this.previewAccessGranted = false;
      this.previewToken = null;
      this.stateService.setState('previewToken', null);
      return false;
    }

    this.previewToken = token;
    this.stateService.setState('previewToken', token);
    //TODO: consider security implications of storing token solutions
    this.setPreviewMode(mode);
    this.previewAccessGranted = true;
    return true;
  }

  private async restorePreviewAccessToken(mode: 'preview' | 'review'): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const sessionToken = sessionStorage.getItem('previewToken');
    if (!sessionToken) {
      return false;
    }

    const hasAccess = await this.applyPreviewAccessToken(sessionToken, mode);
    if (!hasAccess) {
      sessionStorage.removeItem('previewToken');
    }
    return hasAccess;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setFavicon(asset: Asset) {
    const link: HTMLLinkElement | null = this.document.querySelector('#app-favicon');
    if (link) {
      link.href = asset.source || FALLBACK_ICON_URL;
    }
  }

  setWebClipIcon(asset: Asset) {
    const href = asset.imageUrl ?? asset.source ?? FALLBACK_APPLE_TOUCH_ICON_URL;
    this.setHeadLinkTag({
      id: 'app-webclip-icon',
      rel: 'apple-touch-icon',
      href,
    });
  }

  private setHeadLinkTag(params: {
    id: string;
    rel: string;
    href: string;
    type?: string;
    sizes?: string;
  }): void {
    const head = this.document.head;
    if (!head) return;

    let link = this.document.querySelector(`#${params.id}`) as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.id = params.id;
      head.appendChild(link);
    }

    link.rel = params.rel;
    link.href = params.href;

    if (params.type) {
      link.setAttribute('type', params.type);
    } else {
      link.removeAttribute('type');
    }

    if (params.sizes) {
      link.setAttribute('sizes', params.sizes);
    } else {
      link.removeAttribute('sizes');
    }
  }

  private applyThemeColors(colorConfig: ColorConfig): void {
    if(isPlatformBrowser(this.platformId)) {
      const primary50 = colord(colorConfig.mainColor).lighten(0.5).toHex();
      const primary100 = colord(colorConfig.mainColor).lighten(0.4).toHex();
      const primary200 = colord(colorConfig.mainColor).lighten(0.3).toHex();
      const primary300 = colord(colorConfig.mainColor).lighten(0.2).toHex();
      const primary400 = colord(colorConfig.mainColor).lighten(0.1).toHex();
      const primary500 = colorConfig.mainColor;
      const primary600 = colord(colorConfig.mainColor).darken(0.1).toHex();
      const primary700 = colord(colorConfig.mainColor).darken(0.2).toHex();
      const primary800 = colord(colorConfig.mainColor).darken(0.3).toHex();
      const primary900 = colord(colorConfig.mainColor).darken(0.4).toHex();
      const secondary500 = colorConfig.secondaryColor;
      const secondary700 = colord(colorConfig.secondaryColor).darken(0.2).toHex();

      document.documentElement.style.setProperty('--color-primary-50', primary50);
      document.documentElement.style.setProperty('--color-primary-100', primary100);
      document.documentElement.style.setProperty('--color-primary-200', primary200);
      document.documentElement.style.setProperty('--color-primary-300', primary300);
      document.documentElement.style.setProperty('--color-primary-400', primary400);
      document.documentElement.style.setProperty('--color-primary-500', primary500);
      document.documentElement.style.setProperty('--color-primary-600', primary600);
      document.documentElement.style.setProperty('--color-primary-700', primary700);
      document.documentElement.style.setProperty('--color-primary-800', primary800);
      document.documentElement.style.setProperty('--color-primary-900', primary900);
      document.documentElement.style.setProperty('--color-secondary', secondary500);
      document.documentElement.style.setProperty('--color-secondary-500', secondary500);
      document.documentElement.style.setProperty('--color-secondary-700', secondary700);

      const autoPrimaryText =
        colord(primary500).isDark() ? '#ffffff' : '#111827'; // slate-900
      const autoSecondaryText =
        colord(colorConfig.secondaryColor).isDark() ? '#ffffff' : '#111827';

      document.documentElement.style.setProperty('--color-primary-text',
        colorConfig.mainTextColor ?? autoPrimaryText
      );
      document.documentElement.style.setProperty('--color-secondary-text',
        colorConfig.secondaryTextColor ?? autoSecondaryText
      );
    }
  }

  openCartDrawer() {
    this.readyToDisplay$.pipe(
      filter(ready => ready),
      take(1),
      switchMap(() => this.dataService.query<GetActiveOrderQuery>(GET_ACTIVE_ORDER, {}, 'network-only')),
      map(order => order?.activeOrder?.state),
      take(1)
    ).subscribe(orderState => {
      if (orderState !== null) {
        if (orderState === 'ArrangingPayment') {
          this.router.navigate([this.baseUrl, 'cart']);
        } else {
          this.stateService.setState('cartDrawerOpen', true);
        }
      }
    });
  }

  closeCartDrawer() {
    this.stateService.setState('cartDrawerOpen', false);
  }

  clickOnSearchButton() {
    this.uiState.isSearchBarExpanded = !this.uiState.isSearchBarExpanded;
    this.changeDetector.detectChanges();
  }

  clickOnNavButton() {
    this.uiState.isNavExpanded = !this.uiState.isNavExpanded;
    this.changeDetector.detectChanges();
  }

  toggleMobileMenu() {
    this.uiState.isNavExpanded = !this.uiState.isNavExpanded;
    if (this.uiState.isNavExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    this.changeDetector.detectChanges();
  }

  closeMobileMenu() {
    this.uiState.isNavExpanded = false;
    document.body.style.overflow = '';
    this.changeDetector.detectChanges();
  }

  handleAction(actionName: string) {
    switch (actionName) {
      case 'Contact Us':
        window.location.href = getContactUsMailtoHref(this.storeInfo.supportEmail);
        break;
    }
  }

  scrollToSection(sectionId: string) {
    const element = this.document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  navigateToPage(navMenuOption: NavMenuOption) {
    this.logger.debug(`navigateToPage:${navMenuOption}`);
    navigateByNavigationItem(this.router, navMenuOption, this.baseUrl || '', this.storeInfo.supportEmail);
  }

  private loadClarityScript(externalServicesSettings: ExternalServicesSettings): void {
    if (!isPlatformBrowser(this.platformId) || !externalServicesSettings?.clarity?.enabled) {
      return;
    }
    let clarityTrackingId: string = environment.clarityTrackingId || '';
    if (externalServicesSettings?.clarity?.enabled && externalServicesSettings?.clarity?.trackingId) {
      clarityTrackingId = externalServicesSettings?.clarity?.trackingId;
      this.logger.debug('clarity enabled', clarityTrackingId);
    }
    const logger = this.logger;
    (function (c: any, l: Document, a: string, r: string, i: string) {
      c[a] = c[a] || function () {
        // clarity snippet uses arguments; use rest params to satisfy lint while preserving behavior
        // eslint-disable-next-line prefer-rest-params
        const args = Array.prototype.slice.call(arguments);
        (c[a].q = c[a].q || []).push(args);
      };

      const t = l.createElement(r) as HTMLScriptElement;
      t.async = true;
      t.src = "https://www.clarity.ms/tag/" + i;

      const y = l.getElementsByTagName(r)[0];
      if (y && y.parentNode) {
        y.parentNode.insertBefore(t, y);
        logger.debug('loadClarityScript done');
      }
    })(window, document, "clarity", "script", clarityTrackingId);
  }

  private loadTawkioScript(externalServicesSettings: ExternalServicesSettings): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const embedChatId = externalServicesSettings?.tawkio?.embedChatId?.trim();
    const legacyChatId = externalServicesSettings?.tawkio?.chatId?.trim();
    const chatId = embedChatId || legacyChatId;
    if (!externalServicesSettings?.tawkio?.enabled || !chatId) {
      return;
    }

    if (this.document.getElementById('tawkio-widget-script')) {
      return;
    }

    const windowRef = this.document.defaultView;
    if (windowRef) {
      windowRef.Tawk_API = windowRef.Tawk_API || {};
      windowRef.Tawk_LoadStart = new Date();
    }

    const script = this.document.createElement('script');
    script.id = 'tawkio-widget-script';
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://embed.tawk.to/${chatId}`;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');

    const firstScript = this.document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      this.document.head.appendChild(script);
    }
    this.logger.debug('loadTawkioScript done');
  }

  refresh() {
    if (typeof window !== 'undefined') window.location.reload();
  }
}
